import { InterviewType } from "../../graphql/generated/graphql";
import { CONTACT_FORM_URL, PRIVACY_POLICY_URL } from "../../lib/mentoUrls";

export type ScheduleMailUseCase = "COACHING" | "BRICKS" | "STAKEHOLDER";

/**
 * system: システムが自動解決 / config: この画面の設定値を参照 /
 * block: 送信時にシステムが算出する複数行の自動生成ブロック（対応状況・未対応者リスト等）
 */
export type ScheduleMailParameterKind = "system" | "config" | "block";

/** サジェスト・参照リストのグループ見出しラベル */
export const SCHEDULE_MAIL_PARAMETER_KIND_LABELS: Record<
  ScheduleMailParameterKind,
  string
> = {
  system: "システムが自動で埋める",
  config: "この画面の設定値が入る",
  block: "送信時に自動生成されるブロック",
};

// サジェスト・参照リストでグループを表示する順序（種別の並び順の単一の正）
export const SCHEDULE_MAIL_PARAMETER_KIND_ORDER: ScheduleMailParameterKind[] = [
  "system",
  "config",
  "block",
];

export interface ScheduleMailParameterDefinition {
  /** トークン名（@ を除く） */
  name: string;
  /** サジェストに表示する説明 */
  label: string;
  kind: ScheduleMailParameterKind;
  /** 送信時に実際に置き換わる値の例・説明（編集画面の参照リスト用） */
  example: string;
  /** ステークホルダーインタビューでしか値が入らないため、それ以外では参照リストに出さない */
  stakeholderOnly?: boolean;
  /**
   * ユースケースによって誰を指すかが変わるパラメータの言い換え。
   * どのユースケースも対等に扱い、載っていないものは既定の label / example を使う
   */
  wordingByUseCase?: Partial<
    Record<
      ScheduleMailUseCase,
      Partial<Pick<ScheduleMailParameterDefinition, "label" | "example">>
    >
  >;
}

export const SCHEDULE_MAIL_PARAMETERS: ScheduleMailParameterDefinition[] = [
  {
    name: "system_company_name",
    label: "会社名",
    kind: "system",
    example: "例: 株式会社mento",
  },
  {
    name: "system_interviewee_name",
    label: "対象者の氏名",
    kind: "system",
    example: "例: 課地世 花子",
    // 「対象者」はステークホルダーだと語られる側（受け取る人）とも読めるため名指しする
    wordingByUseCase: {
      STAKEHOLDER: {
        label: "回答者（語る人）の氏名",
        example: "例: 部地世 太郎",
      },
    },
  },
  {
    name: "system_interview_title",
    label: "インタビュータイトル（ターム情報）",
    kind: "system",
    example: "例: 効果実感インタビュー",
  },
  {
    name: "system_reservation_url",
    label: "対象者ごとの予約URL",
    kind: "system",
    example: "対象者ごとに固有の予約ページURL",
    wordingByUseCase: {
      STAKEHOLDER: {
        label: "回答者ごとの予約ページURL",
        example: "回答者ごとに固有の予約ページURL",
      },
    },
  },
  {
    name: "system_recipient_names",
    label: "お聞きしたい対象の方（複数）",
    kind: "system",
    stakeholderOnly: true,
    example: "回答者が語る対象の氏名を「・課地世 花子 さま」の箇条書きで展開",
  },
  {
    name: "system_interviewee_type",
    label: "対象者の役割種別",
    kind: "system",
    example:
      "「管理職」「メンバー」「管理職かつメンバー」のいずれか（配属から自動判定）",
    wordingByUseCase: { STAKEHOLDER: { label: "回答者の役割種別" } },
  },
  {
    name: "interview_time",
    label: "所要時間（分）",
    kind: "config",
    example: "上の「所要時間（分）」の値",
  },
  {
    name: "start_date",
    label: "実施時期（開始）",
    kind: "config",
    example: "上の「実施時期」の開始日（例: 2026年7月22日）",
  },
  {
    name: "end_date",
    label: "実施時期（終了）",
    kind: "config",
    example: "上の「実施時期」の終了日（例: 2026年7月31日）",
  },
  {
    name: "reservation_deadline",
    label: "回答期限日",
    kind: "config",
    example: "上の「回答期限日」の値（例: 2026年7月18日）",
  },
];

const DEFAULT_TITLE =
  "【@system_company_name】効果実感インタビュー日程調整のお願い";

const COACHING_DESCRIPTION = `@system_company_name
@system_interviewee_name 様

平素よりmentoをご愛顧いただきまして、誠にありがとうございます。

本日はご活用いただいておりますコーチングの
@system_interview_title日程調整のためにご連絡をさせていただきました。

本インタビューは下記のプライバシーポリシーのもとで実施いたします。
${PRIVACY_POLICY_URL}

プロジェクトの振り返りのため、非常に重要なインタビューです。
ご多用のところ恐れ入りますが、ご協力をいただきますようお願いいたします。

=============================================

【実施概要】

■ 目的
本プロジェクトの振り返りのために、下記事項についてインタビューを実施いたします。
1. mentoならびにコーチングをご活用いただき、感じた価値について
2. mentoならびにコーチング、本プロジェクト関するフィードバックや改善点について

■ 実施場所・時間
場所：オンライン（Zoomを利用予定、日程調整後に詳細をご案内いたします）
時間：@interview_time 分

■ 実施時期
@start_date 〜 @end_date

■ 日程調整の期日
@reservation_deadline まで


【ご依頼事項】

早速ではございますが、インタビュー日程調整をさせていただきたく存じます。
ご都合の良い日時を以下のURLよりご選択いただけますと幸いです。

▼ 日程調整用URL
@system_reservation_url

=============================================

ご予約を進めていただくにあたり、ご説明不足な点やご不明点がありましたら、
以下カスタマーサポート窓口までお知らせくださいませ。
support@mentohelp.zendesk.com

何卒よろしくお願いいたします。`;

const BRICKS_DESCRIPTION = `@system_company_name
@system_interviewee_name 様

平素よりmentoをご愛顧いただきまして、誠にありがとうございます。

本日はご活用いただいておりますマネジメントAIに関しまして
@system_interview_title日程調整のためにご連絡をさせていただきました。

本インタビューは下記のプライバシーポリシーのもとで実施いたします。
${PRIVACY_POLICY_URL}

@system_interviewee_name様の実際のご活用状況やご意見を直接お伺いし、
今後のサービス向上に活かしてまいりたいと考えております。
ご多用のところ大変恐れ入りますが、ぜひご協力いただけますと幸いです。

=============================================

【実施概要】

■ 目的
下記事項について「@system_interviewee_typeとして利用している所感」を中心にインタビューを実施いたします。
1. マネジメントAIをご活用いただき、感じた価値や活用状況について
2. マネジメントAIに関するフィードバックや改善のご要望について

■ 実施場所・時間
場所：オンライン（Zoomを利用予定、日程調整後に詳細をご案内いたします）
時間：@interview_time 分

■ 実施時期
@start_date 〜 @end_date

■ 日程調整の期日
@reservation_deadline まで


【ご依頼事項】

早速ではございますが、インタビュー日程の調整をさせていただきたく存じます。
ご都合の良い日時を以下のURLよりご選択いただけますと幸いです。

▼ 日程調整用URL
@system_reservation_url

=============================================

ご予約を進めていただくにあたり、ご説明不足な点やご不明点がありましたら、
以下カスタマーサポート窓口までお知らせくださいませ。
support@mentohelp.zendesk.com

何卒よろしくお願いいたします。`;

const STAKEHOLDER_TITLE = "mento｜周囲インタビュー日程調整のお願い";

// coaching の enterprise_stakeholder_interview_schedule_request を移植したもの。
// 予約URLは Spir から自社の予約ページに変わり、対象者の列挙は差し込みトークンに置き換えている
const STAKEHOLDER_DESCRIPTION = `@system_interviewee_name さま

お世話になっております。株式会社mentoです。

この度は弊社のコーチングサービスをご導入いただきありがとうございます。
本日は、コーチング実施にあたってのインタビューにむけて日程調整をさせていただきたくメールいたしました。

【@system_interviewee_name さまにご対応いただきたいこと】
下記のURLをクリックしていただき、インタビューの日程調整をお願いします。
@system_reservation_url

本インタビューは下記のプライバシーポリシーのもとで実施いたします。
ご同意いただける場合のみ、日程調整をお願いします。
${PRIVACY_POLICY_URL}

【インタビューの目的】
・コーチング施策の効果最大化
・コーチング施策の効果計測

【@system_interviewee_name さまにお聞きしたい対象の方】
@system_recipient_names

【実施概要】
場所：オンライン（Zoomを利用予定、日程調整後に詳細をご案内いたします）
時間：@interview_time 分
実施時期：@start_date 〜 @end_date
日程調整の期日：@reservation_deadline まで

インタビューの内容につきましては、文字起こし及び分析の後、実名にて本コーチング施策の関連者に展開いたします。
万が一、この日程でご都合が悪くなった時には、下記のURLよりご連絡いただければと思います。
${CONTACT_FORM_URL}

何卒よろしくお願いいたします。`;

export const SCHEDULE_MAIL_DEFAULTS: Record<
  ScheduleMailUseCase,
  { title: string; description: string }
> = {
  COACHING: { title: DEFAULT_TITLE, description: COACHING_DESCRIPTION },
  BRICKS: { title: DEFAULT_TITLE, description: BRICKS_DESCRIPTION },
  STAKEHOLDER: {
    title: STAKEHOLDER_TITLE,
    description: STAKEHOLDER_DESCRIPTION,
  },
};

export const SCHEDULE_MAIL_USE_CASE_LABELS: Record<
  ScheduleMailUseCase,
  string
> = {
  COACHING: "コーチング",
  BRICKS: "Bricks（マネジメントAI）",
  STAKEHOLDER: "ステークホルダー",
};

/**
 * タームの種別で選べるユースケースを決める。
 *
 * ステークホルダー向けの文面は「誰について語るか」の差し込みを前提にしているため、
 * 従業員インタビューでは選ばせない（値が埋まらず空行になる）。
 */
export function scheduleMailUseCasesFor(
  interviewType: InterviewType,
): ScheduleMailUseCase[] {
  return interviewType === InterviewType.Stakeholder
    ? ["STAKEHOLDER"]
    : ["COACHING", "BRICKS"];
}

/**
 * 参照リスト・サジェストに出すパラメータ。ステークホルダー専用のものは種別で出し分け、
 * ユースケースごとの言い換えを当てたうえで返す
 */
export function scheduleMailParametersFor(
  useCase: ScheduleMailUseCase,
): ScheduleMailParameterDefinition[] {
  const visible =
    useCase === "STAKEHOLDER"
      ? SCHEDULE_MAIL_PARAMETERS
      : SCHEDULE_MAIL_PARAMETERS.filter(
          (parameter) => !parameter.stakeholderOnly,
        );
  return visible.map(({ wordingByUseCase, ...parameter }) => ({
    ...parameter,
    ...wordingByUseCase?.[useCase],
  }));
}

// スポンサー担当者（人事）向けリマインドメールのパラメータ。
// 対象者本人向け（SCHEDULE_MAIL_PARAMETERS）とは別セットで、設定値参照系は使わない。
export const SPONSOR_REMINDER_MAIL_PARAMETERS: ScheduleMailParameterDefinition[] =
  [
    {
      name: "system_company_name",
      label: "会社名",
      kind: "system",
      example: "例: 株式会社mento",
    },
    {
      name: "system_interview_title",
      label: "インタビュータイトル（ターム情報）",
      kind: "system",
      example: "例: 効果実感インタビュー",
    },
    {
      name: "system_mail_send_date",
      label: "メール送信日",
      kind: "system",
      example: "送信された日付（例: 2026年7月18日）",
    },
    {
      name: "system_response_status",
      label: "対応状況",
      kind: "block",
      example: "対象者総数・対応完了・未回答の人数を送信時に集計して表示",
    },
    {
      name: "system_pending_interviewees",
      label: "未対応者リスト",
      kind: "block",
      example: "未対応者の氏名を送信時に列挙して表示",
    },
  ];

const SPONSOR_REMINDER_DEFAULT_TITLE =
  "【@system_company_name】@system_interview_titleの対応状況のご報告とお願い";

// デフォルト文言はコーチング/Bricks共通で1種（正本: Notion「スポンサー向け リマインドメール」）
const SPONSOR_REMINDER_DEFAULT_DESCRIPTION = `@system_company_name
ご担当者様

平素よりmentoをご愛顧いただきまして、誠にありがとうございます。

現在進行中のコーチング導入プロジェクトにつきまして、対象者の皆様にお願いしております
@system_interview_titleの状況をご報告いたします。

コーチングの効果を正しく測定し、プログラムをより良くしていくための重要なデータとなります。
現在、一部の対象者様が未対応となっておりますので、
大変お手数ですが、ご担当者様より改めて対応リマインドをご協力いただけますと幸いです。

=============================================

■ 現在の対応状況（@system_mail_send_date現在）

@system_response_status

■ 未対応者リスト

@system_pending_interviewees

=============================================

ご不明点がありましたら、担当営業・カスタマーサクセスまでご連絡いただけますと幸いです。

お忙しいところ恐縮ですが、何卒ご協力のほどよろしくお願い申し上げます。`;

export const SPONSOR_REMINDER_MAIL_DEFAULT = {
  title: SPONSOR_REMINDER_DEFAULT_TITLE,
  description: SPONSOR_REMINDER_DEFAULT_DESCRIPTION,
};
